/*
 * Side cart Merge (buy box Phase 2, Dev Handoff section 7). Markup: snippets/cart-merge.liquid
 * per row, #CartMergeConfig and <cart-merge-total> in snippets/cart-drawer.liquid.
 *
 * "Save $X: merge into one 30-serving bag." swaps every pair of 15-serving bags on a row for
 * one 30-serving bag on the SAME plan (an odd bag stays a 15), with Undo.
 *
 * The saving is what THIS cart's total drops by, after its discounts:
 *   one-time      pairs x (2 x price15 - price30)
 *   subscription  plan prices (per unit, as Shopify prices them), then the Skio volume
 *                 discount for the subscription subtotal BEFORE and AFTER the merge - a merge
 *                 lowers the subtotal and can drop the cart under a trigger, so both sides use
 *                 their own tier (Theme settings > Buy box: triggers + volume %)
 *   welcome code  plan prices, then the welcome code % (no volume tier on welcome orders)
 *
 * What was merged is kept in the cart attribute _smk_merged ({ "small|plan": {pairs} }, hidden
 * at checkout), so "Merged. You saved $X.", Undo and the "Merge savings" total line survive a
 * reload; the $X is always priced against the cart as it is now. Cart writes go through the
 * theme's variantCart* functions (assets/smk.js), which re-render the drawer - every element
 * here is rebuilt from that render.
 */
(function () {
  if (customElements.get('cart-merge')) return;

  function config() {
    const el = document.getElementById('CartMergeConfig');
    if (!el) return null;
    try {
      const cfg = JSON.parse(el.textContent);
      let merged = {};
      try {
        /* Liquid outputs cart attributes HTML-escaped (&quot;...) */
        const raw = document.createElement('textarea');
        raw.innerHTML = cfg.merged || '';
        merged = JSON.parse(raw.value || '{}') || {};
      } catch (error) {
        merged = {};
      }
      cfg.records = merged && typeof merged === 'object' ? merged : {};
      return cfg;
    } catch (error) {
      return null;
    }
  }

  function money(cents, format) {
    const value = (Number(cents) || 0) / 100;
    const fmt = format || '${{amount}}';
    const token = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const amount = value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return token ? fmt.replace(token[0], token[1] === 'amount_no_decimals' ? Math.round(value).toString() : amount) : '$' + amount;
  }

  function volumeFor(cfg, cents) {
    let pct = 0;
    (cfg.triggers || []).forEach((trigger, i) => {
      if (cents >= trigger) pct = (cfg.volume || [])[i] || 0;
    });
    return pct;
  }

  /* What the cart total drops by when `pairs` pairs on this row are (merged = already were)
     merged, priced against the cart as it is now - a code applied later changes it. */
  function saving(cfg, row, pairs, merged) {
    const r = Math.round;
    if (!row.plan) return pairs * (2 * row.priceSmall - row.priceBig);

    const drop = pairs * (2 * row.subSmall - row.subBig);
    const before = merged ? cfg.planSubtotal + drop : cfg.planSubtotal;
    const after = before - drop;
    if (cfg.welcome) {
      /* An order discount: Shopify rounds the amount it takes off DOWN ($109.16 at 24% -> $26.19). */
      const pay = (cents) => cents - Math.floor((cents * cfg.welcomePct) / 100 + 1e-9);
      return pay(before) - pay(after);
    }
    return r((before * (100 - volumeFor(cfg, before))) / 100) - r((after * (100 - volumeFor(cfg, after))) / 100);
  }

  function readRow(el) {
    const d = el.dataset;
    let keys = [];
    try {
      keys = JSON.parse(d.keys || '[]');
    } catch (error) {
      keys = [];
    }
    return {
      flavour: d.flavour || '',
      small: Number(d.small),
      big: Number(d.big),
      sizeBig: Number(d.sizeBig) || 30,
      plan: Number(d.plan) || 0,
      qty: Number(d.qty) || 0,
      priceSmall: Number(d.priceSmall) || 0,
      priceBig: Number(d.priceBig) || 0,
      subSmall: Number(d.subSmall) || 0,
      subBig: Number(d.subBig) || 0,
      keys,
      recordKey: d.small + '|' + (Number(d.plan) || 0),
    };
  }

  /* Set a row (several cart lines Shopify may have split) to `total` units. */
  function groupUpdates(keys, total) {
    const updates = {};
    keys.forEach((line, i) => {
      updates[line.key] = i === 0 ? total : 0;
    });
    return updates;
  }

  function write(adds, updates, records) {
    const add = window.variantCartAdd;
    const update = window.variantCartUpdate;
    if (typeof add !== 'function' || typeof update !== 'function') {
      return Promise.reject(new Error('cart functions not loaded'));
    }
    /* Add first: if the new bag cannot be added (stock), nothing has been taken out. */
    return add(adds, { open: false }).then(() =>
      update({ updates, attributes: { _smk_merged: JSON.stringify(records) } }, { open: false })
    );
  }

  class CartMerge extends HTMLElement {
    connectedCallback() {
      this.cfg = config();
      if (!this.cfg) return;
      this.row = readRow(this);
      this.render();
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-merge-go]')) this.merge();
        else if (event.target.closest('[data-merge-undo]')) this.undo();
      });
    }

    get pairs() {
      return Math.floor(this.row.qty / 2);
    }

    get record() {
      const rec = this.cfg.records[this.row.recordKey];
      return rec && rec.pairs > 0 ? rec : null;
    }

    render() {
      const text = this.querySelector('[data-merge-text]');
      const fmt = this.cfg.moneyFormat;
      if (this.dataset.mode === 'offer') {
        const pairs = this.pairs;
        const amount = saving(this.cfg, this.row, pairs);
        if (pairs < 1 || amount <= 0) {
          this.hidden = true;
          return;
        }
        const bags = pairs === 1 ? 'one ' + this.row.sizeBig + '-serving bag' : pairs + ' ' + this.row.sizeBig + '-serving bags';
        text.textContent = 'Save ' + money(amount, fmt) + ': merge into ' + bags + '. Same servings.';
        this.hidden = false;
        return;
      }

      /* merged: only rows that really came from a merge, and no more pairs than are left */
      const rec = this.record;
      if (!rec || this.row.qty < 1) {
        this.hidden = true;
        return;
      }
      const pairs = Math.min(rec.pairs, this.row.qty);
      const saved = saving(this.cfg, this.row, pairs, true);
      text.textContent = '✓ Merged. You saved ' + money(saved, fmt) + '.';
      this.hidden = false;
    }

    busy(on, message) {
      this.classList.toggle('is-busy', on);
      this.querySelectorAll('button').forEach((b) => {
        b.disabled = on;
      });
      if (message) {
        const text = this.querySelector('[data-merge-text]');
        if (text) text.textContent = message;
      }
    }

    merge() {
      const row = this.row;
      const pairs = this.pairs;
      if (pairs < 1) return;
      const records = Object.assign({}, this.cfg.records);
      const prev = records[row.recordKey] || { pairs: 0 };
      records[row.recordKey] = { pairs: prev.pairs + pairs };

      const item = { id: row.big, quantity: pairs };
      if (row.plan) item.selling_plan = row.plan;

      this.busy(true);
      write(item, groupUpdates(row.keys, row.qty - 2 * pairs), records).catch((error) => {
        console.error('cart-merge', error);
        this.busy(false, 'Could not merge right now. Please try again.');
      });
    }

    undo() {
      const row = this.row;
      const rec = this.record;
      if (!rec) return;
      const pairs = Math.min(rec.pairs, row.qty);
      if (pairs < 1) return;

      const records = Object.assign({}, this.cfg.records);
      const left = rec.pairs - pairs;
      if (left > 0) records[row.recordKey] = { pairs: left };
      else delete records[row.recordKey];

      const item = { id: row.small, quantity: 2 * pairs };
      if (row.plan) item.selling_plan = row.plan;

      this.busy(true);
      write(item, groupUpdates(row.keys, row.qty - pairs), records).catch((error) => {
        console.error('cart-merge', error);
        this.busy(false, 'Could not undo right now. Please try again.');
      });
    }
  }

  /* "Merge savings -$X" in the totals: the records whose merged bags are still in the cart. */
  class CartMergeTotal extends HTMLElement {
    connectedCallback() {
      const cfg = config();
      if (!cfg) return;
      let total = 0;
      document.querySelectorAll('cart-merge[data-mode="merged"]').forEach((el) => {
        const row = readRow(el);
        const rec = cfg.records[row.recordKey];
        if (!rec || rec.pairs < 1 || row.qty < 1) return;
        const pairs = Math.min(rec.pairs, row.qty);
        total += saving(cfg, row, pairs, true);
      });
      const value = this.querySelector('[data-merge-total]');
      if (value) value.textContent = '−' + money(total, cfg.moneyFormat);
      this.hidden = total <= 0;
    }
  }

  customElements.define('cart-merge', CartMerge);
  if (!customElements.get('cart-merge-total')) customElements.define('cart-merge-total', CartMergeTotal);
})();
